import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Search, 
  Package, 
  AlertTriangle, 
  Clock, 
  Ban,
  Plus,
  Download,
  Filter,
  QrCode,
  FileText,
  TrendingUp,
  Calendar,
  X,
  MapPin,
  Truck,
  ShoppingCart,
  History,
  AlertCircle,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface LotBatchSerial {
  id: string;
  type: "lot" | "batch" | "serial";
  number: string;
  stockItemCode: string;
  stockItemName: string;
  quantity: number;
  originalQuantity: number;
  location: string;
  manufacturingDate: Date;
  expiryDate: Date | null;
  supplier: string;
  purchaseOrderRef: string;
  status: "active" | "expired" | "recalled" | "quarantined" | "sold";
  notes: string;
  certificateOfAnalysis?: string;
  qualityTestDate?: Date;
  qualityTestResult?: "pass" | "fail" | "pending";
}

interface MovementHistory {
  id: string;
  date: Date;
  type: "purchase" | "sale" | "transfer" | "adjustment";
  quantity: number;
  location: string;
  reference: string;
  user: string;
}

interface FEFORecommendation {
  lotBatchSerial: LotBatchSerial;
  daysUntilExpiry: number;
  priority: "critical" | "high" | "medium";
  recommendedAction: string;
}

// Mock data
const MOCK_DATA: LotBatchSerial[] = [
  {
    id: "1",
    type: "lot",
    number: "LOT-2024-001",
    stockItemCode: "WGT-A",
    stockItemName: "Widget A",
    quantity: 100,
    originalQuantity: 150,
    location: "Warehouse A",
    manufacturingDate: new Date("2024-01-15"),
    expiryDate: new Date("2025-01-15"),
    supplier: "Supplier A Ltd",
    purchaseOrderRef: "PO-2024-001",
    status: "active",
    notes: "Quality tested and approved",
    certificateOfAnalysis: "COA-2024-001.pdf",
    qualityTestDate: new Date("2024-01-16"),
    qualityTestResult: "pass"
  },
  {
    id: "2",
    type: "batch",
    number: "BATCH-456",
    stockItemCode: "PART-C",
    stockItemName: "Component C",
    quantity: 50,
    originalQuantity: 50,
    location: "Warehouse B",
    manufacturingDate: new Date("2023-12-20"),
    expiryDate: new Date("2024-03-20"),
    supplier: "Supplier B Corp",
    purchaseOrderRef: "PO-2023-456",
    status: "active",
    notes: "Expiring soon - prioritise for use",
    qualityTestDate: new Date("2023-12-21"),
    qualityTestResult: "pass"
  },
  {
    id: "3",
    type: "serial",
    number: "SN-001234",
    stockItemCode: "GDG-B",
    stockItemName: "Gadget B",
    quantity: 0,
    originalQuantity: 1,
    location: "Sold",
    manufacturingDate: new Date("2024-02-01"),
    expiryDate: null,
    supplier: "Supplier A Ltd",
    purchaseOrderRef: "PO-2024-023",
    status: "sold",
    notes: "Sold to Customer XYZ on 2024-02-15"
  },
  {
    id: "4",
    type: "lot",
    number: "LOT-2023-789",
    stockItemCode: "WGT-B",
    stockItemName: "Widget B",
    quantity: 25,
    originalQuantity: 100,
    location: "Warehouse A",
    manufacturingDate: new Date("2023-11-01"),
    expiryDate: new Date("2024-02-01"),
    supplier: "Supplier C Inc",
    purchaseOrderRef: "PO-2023-789",
    status: "expired",
    notes: "Expired - pending disposal"
  },
  {
    id: "5",
    type: "batch",
    number: "BATCH-2024-100",
    stockItemCode: "RAW-X",
    stockItemName: "Raw Material X",
    quantity: 200,
    originalQuantity: 200,
    location: "Warehouse B",
    manufacturingDate: new Date("2024-01-10"),
    expiryDate: new Date("2025-01-10"),
    supplier: "Global Supplies Co",
    purchaseOrderRef: "PO-2024-100",
    status: "active",
    notes: "Fresh batch - excellent quality",
    certificateOfAnalysis: "COA-2024-100.pdf",
    qualityTestDate: new Date("2024-01-11"),
    qualityTestResult: "pass"
  },
  {
    id: "6",
    type: "serial",
    number: "SN-002456",
    stockItemCode: "GDG-B",
    stockItemName: "Gadget B",
    quantity: 1,
    originalQuantity: 1,
    location: "Warehouse A",
    manufacturingDate: new Date("2024-02-10"),
    expiryDate: null,
    supplier: "Supplier A Ltd",
    purchaseOrderRef: "PO-2024-045",
    status: "active",
    notes: "In stock - available for sale"
  },
  {
    id: "7",
    type: "lot",
    number: "LOT-2024-050",
    stockItemCode: "FOOD-A",
    stockItemName: "Food Product A",
    quantity: 75,
    originalQuantity: 100,
    location: "Cold Storage",
    manufacturingDate: new Date("2024-01-20"),
    expiryDate: new Date("2024-02-28"),
    supplier: "Food Supplier Ltd",
    purchaseOrderRef: "PO-2024-050",
    status: "active",
    notes: "Temperature controlled - handle with care",
    qualityTestDate: new Date("2024-01-21"),
    qualityTestResult: "pass"
  },
  {
    id: "8",
    type: "batch",
    number: "BATCH-RECALL-01",
    stockItemCode: "PART-D",
    stockItemName: "Component D",
    quantity: 30,
    originalQuantity: 50,
    location: "Quarantine Area",
    manufacturingDate: new Date("2023-10-15"),
    expiryDate: new Date("2024-10-15"),
    supplier: "Supplier B Corp",
    purchaseOrderRef: "PO-2023-300",
    status: "recalled",
    notes: "Recalled due to quality issue - awaiting return to supplier",
    qualityTestDate: new Date("2023-10-16"),
    qualityTestResult: "fail"
  },
];

const MOCK_MOVEMENT_HISTORY: { [key: string]: MovementHistory[] } = {
  "1": [
    {
      id: "m1",
      date: new Date("2024-01-15"),
      type: "purchase",
      quantity: 150,
      location: "Warehouse A",
      reference: "PO-2024-001",
      user: "John Smith"
    },
    {
      id: "m2",
      date: new Date("2024-01-20"),
      type: "sale",
      quantity: -30,
      location: "Warehouse A",
      reference: "SO-2024-015",
      user: "Sarah Jones"
    },
    {
      id: "m3",
      date: new Date("2024-01-25"),
      type: "sale",
      quantity: -20,
      location: "Warehouse A",
      reference: "SO-2024-023",
      user: "Mike Wilson"
    }
  ]
};

const STOCK_ITEMS = [
  { code: "WGT-A", name: "Widget A" },
  { code: "WGT-B", name: "Widget B" },
  { code: "GDG-B", name: "Gadget B" },
  { code: "PART-C", name: "Component C" },
  { code: "RAW-X", name: "Raw Material X" },
  { code: "FOOD-A", name: "Food Product A" },
];

const LOCATIONS = ["Warehouse A", "Warehouse B", "Cold Storage", "Retail Store", "Distribution Center"];
const SUPPLIERS = ["Supplier A Ltd", "Supplier B Corp", "Supplier C Inc", "Global Supplies Co", "Food Supplier Ltd"];

export function LotBatchSerialPage() {
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("lot-batch-serial");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedItem, setSelectedItem] = useState<LotBatchSerial | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showFEFODialog, setShowFEFODialog] = useState(false);

  // Form state for adding new item
  const [newItem, setNewItem] = useState({
    type: "lot" as "lot" | "batch" | "serial",
    number: "",
    stockItemCode: "",
    quantity: "",
    location: "",
    manufacturingDate: "",
    expiryDate: "",
    supplier: "",
    purchaseOrderRef: "",
    notes: ""
  });

  // Calculate summary statistics
  const stats = useMemo(() => {
    const activeLots = MOCK_DATA.filter(item => item.status === "active").length;
    const expiringSoon = MOCK_DATA.filter(item => {
      if (!item.expiryDate || item.status !== "active") return false;
      const daysUntilExpiry = Math.floor((item.expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
    }).length;
    const expired = MOCK_DATA.filter(item => item.status === "expired").length;
    const recalled = MOCK_DATA.filter(item => item.status === "recalled").length;

    return { activeLots, expiringSoon, expired, recalled };
  }, []);

  // Calculate FEFO recommendations
  const fefoRecommendations = useMemo((): FEFORecommendation[] => {
    return MOCK_DATA
      .filter(item => item.expiryDate && item.status === "active" && item.quantity > 0)
      .map(item => {
        const daysUntilExpiry = Math.floor((item.expiryDate!.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        let priority: "critical" | "high" | "medium" = "medium";
        let recommendedAction = "Use in normal rotation";

        if (daysUntilExpiry <= 7) {
          priority = "critical";
          recommendedAction = "USE IMMEDIATELY - Expires within 7 days";
        } else if (daysUntilExpiry <= 14) {
          priority = "high";
          recommendedAction = "Prioritise for next orders";
        } else if (daysUntilExpiry <= 30) {
          priority = "high";
          recommendedAction = "Include in upcoming shipments";
        }

        return {
          lotBatchSerial: item,
          daysUntilExpiry,
          priority,
          recommendedAction
        };
      })
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_DATA;

    // Filter by active tab
    if (activeTab === "lots") {
      filtered = filtered.filter(item => item.type === "lot");
    } else if (activeTab === "batches") {
      filtered = filtered.filter(item => item.type === "batch");
    } else if (activeTab === "serials") {
      filtered = filtered.filter(item => item.type === "serial");
    } else if (activeTab === "expiring") {
      filtered = filtered.filter(item => {
        if (!item.expiryDate || item.status !== "active") return false;
        const daysUntilExpiry = Math.floor((item.expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
      });
    } else if (activeTab === "recalls") {
      filtered = filtered.filter(item => item.status === "recalled");
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.stockItemCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.stockItemName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Filter by type
    if (typeFilter !== "all") {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, typeFilter, activeTab]);

  // Format date
  const formatDate = (date: Date | null) => {
    if (!date) return "-";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Get days until expiry
  const getDaysUntilExpiry = (expiryDate: Date | null) => {
    if (!expiryDate) return null;
    const days = Math.floor((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success" accent="success">Active</Badge>;
      case "expired":
        return <Badge variant="destructive">Expired</Badge>;
      case "recalled":
        return <Badge variant="destructive">Recalled</Badge>;
      case "quarantined":
        return <Badge variant="secondary">Quarantined</Badge>;
      case "sold":
        return <Badge variant="secondary">Sold</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get type badge
  const getTypeBadge = (type: string) => {
    switch (type) {
      case "lot":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Lot</Badge>;
      case "batch":
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Batch</Badge>;
      case "serial":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Serial</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  // Get expiry badge
  const getExpiryBadge = (expiryDate: Date | null, status: string) => {
    if (!expiryDate || status !== "active") return null;
    
    const days = getDaysUntilExpiry(expiryDate);
    if (days === null) return null;

    if (days < 0) {
      return <Badge variant="destructive" className="ml-2">Expired</Badge>;
    } else if (days <= 7) {
      return <Badge variant="destructive" className="ml-2">Expires in {days}d</Badge>;
    } else if (days <= 30) {
      return <Badge variant="secondary" className="ml-2 bg-yellow-50 text-yellow-700 border-yellow-200">Expires in {days}d</Badge>;
    }
    return null;
  };

  // Handle row click
  const handleRowClick = (item: LotBatchSerial) => {
    setSelectedItem(item);
    setShowDetailDrawer(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setNewItem({
      type: "lot",
      number: "",
      stockItemCode: "",
      quantity: "",
      location: "",
      manufacturingDate: "",
      expiryDate: "",
      supplier: "",
      purchaseOrderRef: "",
      notes: ""
    });
    setShowAddDialog(true);
  };

  // Handle save new item
  const handleSaveNewItem = () => {
    // Validation
    if (!newItem.number || !newItem.stockItemCode || !newItem.quantity) {
      toast.error("Please fill in all required fields");
      return;
    }

    // In real app, this would call an API
    toast.success(`${newItem.type.charAt(0).toUpperCase() + newItem.type.slice(1)} ${newItem.number} created successfully`);
    setShowAddDialog(false);
  };

  // Get priority badge for FEFO
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "critical":
        return <Badge variant="destructive">Critical</Badge>;
      case "high":
        return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">High</Badge>;
      case "medium":
        return <Badge variant="outline">Medium</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Lot/Batch/Serial Tracking</h1>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              🟡 Demo Mode
            </Badge>
          </div>
          <p className="text-muted-foreground mt-2">
            Track and manage inventory with lot numbers, batch codes, and serial numbers for full traceability
          </p>
        </div>
        <HelpButton onClick={toggleHelp} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StaticMetricCardAlt
          title="Active Items"
          value={stats.activeLots.toString()}
          icon={Package}
          variant="info"
          changeLabel="Lots, batches & serials in stock"
        />
        <StaticMetricCardAlt
          title="Expiring Soon"
          value={stats.expiringSoon.toString()}
          icon={Clock}
          variant="warning"
          changeLabel="Items expiring within 30 days"
        />
        <StaticMetricCardAlt
          title="Expired Items"
          value={stats.expired.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="Items requiring action"
        />
        <StaticMetricCardAlt
          title="Recalled Items"
          value={stats.recalled.toString()}
          icon={Ban}
          variant="error"
          changeLabel="Items under recall"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Tracked Inventory</CardTitle>
              <CardDescription>
                View and manage all lot, batch, and serial number tracked items
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button size="sm" onClick={handleAddNew}>
                <Plus className="h-4 w-4 mr-2" />
                Add New
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="all">All Items</TabsTrigger>
              <TabsTrigger value="lots">Lots</TabsTrigger>
              <TabsTrigger value="batches">Batches</TabsTrigger>
              <TabsTrigger value="serials">Serial Numbers</TabsTrigger>
              <TabsTrigger value="expiring">
                Expiring Soon
                {stats.expiringSoon > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-yellow-50 text-yellow-700">
                    {stats.expiringSoon}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="recalls">
                Recalls
                {stats.recalled > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {stats.recalled}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="space-y-4">
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by lot/batch/serial number or stock item..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="recalled">Recalled</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="lot">Lot</SelectItem>
                    <SelectItem value="batch">Batch</SelectItem>
                    <SelectItem value="serial">Serial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Results count */}
              <div className="text-sm text-muted-foreground">
                Showing {filteredData.length} of {MOCK_DATA.length} items
              </div>

              {/* Data Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Number</TableHead>
                      <TableHead>Stock Item</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Mfg Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          No items found matching your criteria
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData.map((item) => (
                        <TableRow 
                          key={item.id} 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleRowClick(item)}
                        >
                          <TableCell>{getTypeBadge(item.type)}</TableCell>
                          <TableCell className="font-medium font-mono">{item.number}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{item.stockItemName}</div>
                              <div className="text-sm text-muted-foreground">{item.stockItemCode}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {item.quantity > 0 ? (
                              <div>
                                <div className="font-medium">{item.quantity}</div>
                                <div className="text-xs text-muted-foreground">
                                  of {item.originalQuantity}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>{item.location}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatDate(item.manufacturingDate)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <div className="flex items-center">
                              {formatDate(item.expiryDate)}
                              {getExpiryBadge(item.expiryDate, item.status)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{item.supplier}</TableCell>
                          <TableCell>{getStatusBadge(item.status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toast.info("QR Code generation coming soon");
                                }}
                              >
                                <QrCode className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toast.info("Document view coming soon");
                                }}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => setShowFEFODialog(true)}
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              FEFO Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              View First-Expired-First-Out picking recommendations for optimal stock rotation
            </p>
            {fefoRecommendations.length > 0 && (
              <div className="mt-2">
                <Badge variant="destructive">{fefoRecommendations.filter(r => r.priority === "critical").length} Critical</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-primary transition-colors">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Traceability Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Generate complete traceability reports for compliance and auditing
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-primary transition-colors">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Ban className="h-5 w-5 text-primary" />
              Initiate Recall
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Start a product recall process and track affected items and customers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-2xl flex flex-col">
          {selectedItem && (
            <>
              <SheetHeader className="bg-muted p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <SheetTitle className="font-mono">{selectedItem.number}</SheetTitle>
                  {getTypeBadge(selectedItem.type)}
                  {getStatusBadge(selectedItem.status)}
                </div>
                <SheetDescription>
                  {selectedItem.stockItemName} ({selectedItem.stockItemCode})
                </SheetDescription>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto p-4 bg-background space-y-6">
                {/* Overview */}
                <div>
                  <h3 className="font-semibold mb-3">Overview</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Current Quantity</Label>
                      <p className="font-medium text-lg">{selectedItem.quantity} units</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Original Quantity</Label>
                      <p className="font-medium text-lg">{selectedItem.originalQuantity} units</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Location</Label>
                      <p className="font-medium flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {selectedItem.location}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Supplier</Label>
                      <p className="font-medium flex items-center gap-1">
                        <Truck className="h-4 w-4" />
                        {selectedItem.supplier}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div>
                  <h3 className="font-semibold mb-3">Important Dates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Manufacturing Date</Label>
                      <p className="font-medium font-mono">{formatDate(selectedItem.manufacturingDate)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Expiry Date</Label>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono">{formatDate(selectedItem.expiryDate)}</p>
                        {getExpiryBadge(selectedItem.expiryDate, selectedItem.status)}
                      </div>
                    </div>
                    {selectedItem.qualityTestDate && (
                      <div>
                        <Label className="text-muted-foreground">Quality Test Date</Label>
                        <p className="font-medium font-mono">{formatDate(selectedItem.qualityTestDate)}</p>
                      </div>
                    )}
                    {selectedItem.qualityTestResult && (
                      <div>
                        <Label className="text-muted-foreground">Quality Test Result</Label>
                        <div className="flex items-center gap-2">
                          {selectedItem.qualityTestResult === "pass" ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="font-medium text-green-600">Passed</span>
                            </>
                          ) : selectedItem.qualityTestResult === "fail" ? (
                            <>
                              <AlertCircle className="h-4 w-4 text-destructive" />
                              <span className="font-medium text-destructive">Failed</span>
                            </>
                          ) : (
                            <>
                              <Clock className="h-4 w-4 text-yellow-600" />
                              <span className="font-medium text-yellow-600">Pending</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reference Information */}
                <div>
                  <h3 className="font-semibold mb-3">Reference Information</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-muted-foreground">Purchase Order</Label>
                      <p className="font-medium font-mono">{selectedItem.purchaseOrderRef}</p>
                    </div>
                    {selectedItem.certificateOfAnalysis && (
                      <div>
                        <Label className="text-muted-foreground">Certificate of Analysis</Label>
                        <Button variant="link" className="h-auto p-0 font-medium">
                          <FileText className="h-4 w-4 mr-1" />
                          {selectedItem.certificateOfAnalysis}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {selectedItem.notes && (
                  <div>
                    <h3 className="font-semibold mb-3">Notes</h3>
                    <p className="text-sm bg-muted p-3 rounded-md">{selectedItem.notes}</p>
                  </div>
                )}

                {/* Movement History */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Movement History
                  </h3>
                  {MOCK_MOVEMENT_HISTORY[selectedItem.id] ? (
                    <div className="space-y-2">
                      {MOCK_MOVEMENT_HISTORY[selectedItem.id].map((movement) => (
                        <div key={movement.id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {movement.type === "purchase" && <ShoppingCart className="h-4 w-4 text-green-600" />}
                              {movement.type === "sale" && <ShoppingCart className="h-4 w-4 text-blue-600" />}
                              {movement.type === "transfer" && <Truck className="h-4 w-4 text-purple-600" />}
                              <span className="font-medium capitalize">{movement.type}</span>
                            </div>
                            <span className="text-sm text-muted-foreground font-mono">
                              {formatDate(movement.date)}
                            </span>
                          </div>
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Quantity:</span>
                              <span className={`font-medium font-mono ${movement.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Location:</span>
                              <span className="font-medium">{movement.location}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Reference:</span>
                              <span className="font-medium font-mono">{movement.reference}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">User:</span>
                              <span className="font-medium">{movement.user}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No movement history available</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" className="flex-1">
                    <QrCode className="h-4 w-4 mr-2" />
                    Generate QR Code
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <FileText className="h-4 w-4 mr-2" />
                    View Documents
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add New Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lot/Batch/Serial</DialogTitle>
            <DialogDescription>
              Register a new lot, batch, or serial number for tracking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type Selection */}
            <div className="space-y-2">
              <Label>Tracking Type <span className="text-destructive">*</span></Label>
              <Select value={newItem.type} onValueChange={(value: "lot" | "batch" | "serial") => setNewItem({ ...newItem, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lot">Lot</SelectItem>
                  <SelectItem value="batch">Batch</SelectItem>
                  <SelectItem value="serial">Serial Number</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Number */}
            <div className="space-y-2">
              <Label htmlFor="number">
                {newItem.type === "serial" ? "Serial Number" : `${newItem.type.charAt(0).toUpperCase() + newItem.type.slice(1)} Number`}
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="number"
                placeholder={`e.g., ${newItem.type === "serial" ? "SN-001234" : newItem.type === "lot" ? "LOT-2024-001" : "BATCH-456"}`}
                value={newItem.number}
                onChange={(e) => setNewItem({ ...newItem, number: e.target.value })}
              />
            </div>

            {/* Stock Item */}
            <div className="space-y-2">
              <Label htmlFor="stockItem">Stock Item <span className="text-destructive">*</span></Label>
              <Select value={newItem.stockItemCode} onValueChange={(value) => setNewItem({ ...newItem, stockItemCode: value })}>
                <SelectTrigger id="stockItem">
                  <SelectValue placeholder="Select stock item" />
                </SelectTrigger>
                <SelectContent>
                  {STOCK_ITEMS.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.name} ({item.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label htmlFor="quantity">
                Quantity <span className="text-destructive">*</span>
              </Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                placeholder={newItem.type === "serial" ? "1" : "Enter quantity"}
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                disabled={newItem.type === "serial"}
              />
              {newItem.type === "serial" && (
                <p className="text-xs text-muted-foreground">Serial numbers always have quantity of 1</p>
              )}
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select value={newItem.location} onValueChange={(value) => setNewItem({ ...newItem, location: value })}>
                <SelectTrigger id="location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mfgDate">Manufacturing Date</Label>
                <Input
                  id="mfgDate"
                  type="date"
                  value={newItem.manufacturingDate}
                  onChange={(e) => setNewItem({ ...newItem, manufacturingDate: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={newItem.expiryDate}
                  onChange={(e) => setNewItem({ ...newItem, expiryDate: e.target.value })}
                />
              </div>
            </div>

            {/* Supplier */}
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Select value={newItem.supplier} onValueChange={(value) => setNewItem({ ...newItem, supplier: value })}>
                <SelectTrigger id="supplier">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPLIERS.map((supplier) => (
                    <SelectItem key={supplier} value={supplier}>
                      {supplier}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Purchase Order Reference */}
            <div className="space-y-2">
              <Label htmlFor="poRef">Purchase Order Reference</Label>
              <Input
                id="poRef"
                placeholder="e.g., PO-2024-001"
                value={newItem.purchaseOrderRef}
                onChange={(e) => setNewItem({ ...newItem, purchaseOrderRef: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes or comments..."
                rows={3}
                value={newItem.notes}
                onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNewItem}>
              Create {newItem.type.charAt(0).toUpperCase() + newItem.type.slice(1)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FEFO Recommendations Dialog */}
      <Dialog open={showFEFODialog} onOpenChange={setShowFEFODialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              FEFO Recommendations
            </DialogTitle>
            <DialogDescription>
              First-Expired-First-Out picking recommendations to optimise stock rotation and minimise waste
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {fefoRecommendations.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-4" />
                <p className="text-lg font-medium">No items expiring soon</p>
                <p className="text-sm text-muted-foreground">All tracked items have sufficient shelf life</p>
              </div>
            ) : (
              <div className="space-y-3">
                {fefoRecommendations.map((rec) => (
                  <Card key={rec.lotBatchSerial.id} className={
                    rec.priority === "critical" ? "border-destructive" :
                    rec.priority === "high" ? "border-yellow-500" : ""
                  }>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getTypeBadge(rec.lotBatchSerial.type)}
                            <span className="font-mono font-medium">{rec.lotBatchSerial.number}</span>
                            {getPriorityBadge(rec.priority)}
                          </div>
                          <p className="text-sm font-medium">{rec.lotBatchSerial.stockItemName}</p>
                          <p className="text-xs text-muted-foreground">{rec.lotBatchSerial.stockItemCode}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-destructive">{rec.daysUntilExpiry}</p>
                          <p className="text-xs text-muted-foreground">days left</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Quantity:</span>
                          <span className="font-medium ml-1">{rec.lotBatchSerial.quantity} units</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Location:</span>
                          <span className="font-medium ml-1">{rec.lotBatchSerial.location}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Expiry:</span>
                          <span className="font-medium ml-1 font-mono">{formatDate(rec.lotBatchSerial.expiryDate)}</span>
                        </div>
                      </div>

                      <div className={`p-2 rounded-md text-sm ${
                        rec.priority === "critical" ? "bg-destructive/10 text-destructive" :
                        rec.priority === "high" ? "bg-yellow-50 text-yellow-700" :
                        "bg-muted"
                      }`}>
                        <strong>Recommendation:</strong> {rec.recommendedAction}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFEFODialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              toast.success("FEFO report exported successfully");
              setShowFEFODialog(false);
            }}>
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />
    </div>
  );
}
